import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  Divider,
  IconButton,
  useTheme,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TextField,
  Badge,
  LinearProgress,
  Fab,
  Stack,
} from '@mui/material';
import {
  Schedule,
  AccountBalanceWallet,
  Star,
  Phone,
  PlayArrow,
  Pets,
  Check,
  Close,
  Message,
  LocationOn,
  Stop,
  Send,
  PhotoCamera,
  Navigation,
  CheckCircle,
  Person,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import ServiceUpdatesChat from '../components/ServiceUpdatesChat';
import LiveTracking from '../components/LiveTracking';
import { SkeletonBlock, SkeletonCard } from '../components/Skeletons';
import {
  INK, PAPER, MINT, INK_RAISED, INK_LINE, PAPER_MUTED, SHADOW_COBALT, moneySx,
} from '../theme/kiff';

const HandlerDashboard = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Basic state
  const [isAvailable, setIsAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Data state
  const [serviceRequests, setServiceRequests] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [anyHandlerOpportunities, setAnyHandlerOpportunities] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [nearbyRequests, setNearbyRequests] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [searchRadius, setSearchRadius] = useState(5);
  const [maxDistance, setMaxDistance] = useState(5);
  const [handlerLocation, setHandlerLocation] = useState(null);
  const [locationMessage, setLocationMessage] = useState('');
  const [upcomingServices, setUpcomingServices] = useState([]);
  const [completedServices, setCompletedServices] = useState([]);
  const [activeService, setActiveService] = useState(null);
  const [serviceUpdates, setServiceUpdates] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    stats: {},
    upcoming_services: []
  });
  // UI state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [activeServiceDialogOpen, setActiveServiceDialogOpen] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [updatePhoto, setUpdatePhoto] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [serviceDetailsOpen, setServiceDetailsOpen] = useState(false);
  const [selectedServiceDetails, setSelectedServiceDetails] = useState(null);
  const [serviceDetailsUpdates, setServiceDetailsUpdates] = useState([]);

  // Add state for location filter
  // eslint-disable-next-line no-unused-vars
  const [locationSource, setLocationSource] = useState('default_address'); // 'default_address' or 'current_location'

  // Add state for upcoming service details
  const [upcomingServiceDetailsOpen, setUpcomingServiceDetailsOpen] = useState(false);
  const [selectedUpcomingService, setSelectedUpcomingService] = useState(null);

  // Review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [ownerReviewDialogOpen, setOwnerReviewDialogOpen] = useState(false);
  const [reviewData, setReviewData] = useState({});
  const [reviewLoading, setReviewLoading] = useState(false);
  const [ownerReviewData, setOwnerReviewData] = useState({
    punctuality_rating: 0,
    communication_rating: 0,
    pet_behavior_rating: 0,
    comment: '',
    would_walk_for_again: true,
    pets_match_description: true,
    safe_neighborhood: true
  });

  useEffect(() => {
    if (user?.id) {
    fetchDashboardData();
    }
    // Set up location tracking
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => console.log('Location access denied:', error)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    // Auto-refresh every 30 seconds when there's an active service
    let interval;
    if (activeService) {
      interval = setInterval(() => {
        fetchServiceUpdates(activeService.id);
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeService]);

  const fetchDashboardData = async () => {
    if (!user?.id) {
      console.log('User ID not available, skipping dashboard data fetch');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Build params for handler requests
      let handlerRequestParams = `?filter_type=${locationSource}&max_distance=${maxDistance}`;
      if (locationSource === 'current_location' && currentLocation) {
        handlerRequestParams += `&latitude=${currentLocation.latitude}&longitude=${currentLocation.longitude}`;
      }
      const [dashboard, requestsData, servicesResponse, handlerProfile, handlerHistory] = await Promise.all([
        apiService.getHandlerDashboard(),
        apiService.get(`/services/handler-requests/${handlerRequestParams}`),
        apiService.getServiceBookings(),
        apiService.getCurrentUserHandlerProfile(),
        apiService.getHandlerHistory(5)
      ]);
      
      // Handle paginated response - extract results array
      const allServices = servicesResponse.results || servicesResponse || [];
      
      setDashboardData(dashboard);
      
      // Handle new API structure for service requests
      if (requestsData.message) {
        setLocationMessage(requestsData.message);
        setServiceRequests([]);
        setAnyHandlerOpportunities([]);
      } else {
        setServiceRequests(requestsData.available_requests || []);
        setSearchRadius(requestsData.search_radius || 5);
        setHandlerLocation(requestsData.handler_location);
        setLocationMessage('');
      }
      
      setIsAvailable(handlerProfile?.is_available || false);
      
      // Filter upcoming services to exclude 'in_progress' services (those show in active section)
      const upcomingServicesFiltered = (dashboard.upcoming_services || []).filter(
        service => service.status === 'accepted'
      );
      setUpcomingServices(upcomingServicesFiltered);
      
      // Set completed services from history
      setCompletedServices(handlerHistory.history || []);
      
      // Find active service from all services
      const active = allServices.find(service => service.status === 'in_progress');
      if (active) {
        setActiveService(active);
        fetchServiceUpdates(active.id);
      } else {
        setActiveService(null);
      }
      
    } catch (err) {
      if (err.message && (err.message.includes('403') || err.message.includes('Forbidden'))) {
        navigate('/dashboard');
        return;
      }
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceUpdates = async (serviceId) => {
    try {
      const updates = await apiService.getServiceUpdates(serviceId);
      setServiceUpdates(updates);
    } catch (err) {
      console.error('Error fetching service updates:', err);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      setActionLoading(true);
      await apiService.acceptServiceRequest(requestId);
      setSuccess('Service request accepted successfully!');
      fetchDashboardData(); // Refresh data
    } catch (err) {
      setError('Failed to accept service request.');
    } finally {
      setActionLoading(false);
    }
  };



  const handleStartService = async (serviceId) => {
    try {
      setActionLoading(true);
      await apiService.updateServiceBooking(serviceId, { status: 'in_progress' });
      setSuccess('Service started successfully!');
      fetchDashboardData();
    } catch (err) {
      setError('Failed to start service.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteService = async (serviceId) => {
    try {
      setActionLoading(true);
      await apiService.updateServiceBooking(serviceId, { status: 'completed' });
      setSuccess('Service completed successfully!');
      fetchDashboardData();
    } catch (err) {
      setError('Failed to complete service.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleAvailability = async (newAvailability) => {
    try {
      setActionLoading(true);
      await apiService.updateCurrentUserHandlerProfile({ is_available: newAvailability });
      setIsAvailable(newAvailability);
      setSuccess(newAvailability ? 'You are now available for services!' : 'You are now unavailable for services.');
    } catch (err) {
      setError('Failed to update availability status.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDistanceFilterChange = async (newDistance) => {
    setMaxDistance(newDistance);
    try {
      setActionLoading(true);
      const nearbyData = await apiService.getNearbyRequests(newDistance);
      if (nearbyData.message) {
        setNearbyRequests([]);
      } else {
        setNearbyRequests(nearbyData.available_requests || []);
      }
      setSuccess(`Updated search radius to ${newDistance} km`);
    } catch (err) {
      setError('Failed to update distance filter.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendUpdate = async (type = 'message') => {
    if (!activeService || (!updateMessage && !updatePhoto && type !== 'location')) return;
    
    try {
      setActionLoading(true);
      const updateData = {
        update_type: type,
        message: updateMessage,
      };
      
      if (updatePhoto && type === 'photo') {
        updateData.photo = updatePhoto;
      }
      
      if (currentLocation && type === 'location') {
        updateData.latitude = currentLocation.latitude;
        updateData.longitude = currentLocation.longitude;
        updateData.update_type = 'location';
        updateData.message = updateMessage || 'Location update';
      }
      
      await apiService.createServiceUpdate(activeService.id, updateData);
      await fetchServiceUpdates(activeService.id);
      
      setUpdateMessage('');
      setUpdatePhoto(null);
    } catch (err) {
      setError('Failed to send update.');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUpdatePhoto(file);
    }
  };

  const openRequestDialog = (request) => {
    setSelectedRequest(request);
    setRequestDialogOpen(true);
  };

  const handleViewServiceDetails = async (serviceId) => {
    try {
      setActionLoading(true);
      // Fetch service details and updates
      const [serviceDetails, updates] = await Promise.all([
        apiService.getServiceBooking(serviceId),
        apiService.getServiceUpdates(serviceId)
      ]);
      
      setSelectedServiceDetails(serviceDetails);
      setServiceDetailsUpdates(updates);
      setServiceDetailsOpen(true);
    } catch (err) {
      console.error('Error fetching service details:', err);
      setError('Failed to load service details.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewUpcomingServiceDetails = (service) => {
    setSelectedUpcomingService(service);
    setUpcomingServiceDetailsOpen(true);
  };

  const handleCloseServiceDetails = () => {
    setServiceDetailsOpen(false);
    setSelectedServiceDetails(null);
    setServiceDetailsUpdates([]);
  };

  const handleCloseUpcomingServiceDetails = () => {
    setUpcomingServiceDetailsOpen(false);
    setSelectedUpcomingService(null);
  };

  const handleOpenReviewDialog = async (service) => {
    // If the service doesn't have complete owner information, fetch the full details
    if (!service.owner?.id) {
      try {
        const fullServiceDetails = await apiService.getServiceBooking(service.id);
        setSelectedUpcomingService(fullServiceDetails);
      } catch (err) {
        console.error('Error fetching service details:', err);
        setError('Failed to load service details. Please try again.');
        return;
      }
    } else {
      setSelectedUpcomingService(service);
    }
    setReviewDialogOpen(true);
  };

  const handleCloseReviewDialog = () => {
    setReviewDialogOpen(false);
    setReviewData({});
    setSelectedUpcomingService(null);
  };

  const handleOpenOwnerReviewDialog = async (service) => {
    // If the service doesn't have complete owner information, fetch the full details
    if (!service.owner?.id) {
      try {
        const fullServiceDetails = await apiService.getServiceBooking(service.id);
        setSelectedUpcomingService(fullServiceDetails);
      } catch (err) {
        console.error('Error fetching service details:', err);
        setError('Failed to load service details. Please try again.');
        return;
      }
    } else {
      setSelectedUpcomingService(service);
    }
    setOwnerReviewDialogOpen(true);
  };

  const handleCloseOwnerReviewDialog = () => {
    setOwnerReviewDialogOpen(false);
    setOwnerReviewData({
      punctuality_rating: 0,
      communication_rating: 0,
      pet_behavior_rating: 0,
      comment: '',
      would_walk_for_again: true,
      pets_match_description: true,
      safe_neighborhood: true
    });
    setSelectedUpcomingService(null);
  };

  const handleOwnerReviewChange = (field, value) => {
    setOwnerReviewData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitOwnerReview = async () => {
    if (!selectedUpcomingService) return;

    // Check if at least one rating is provided
    if (!ownerReviewData.punctuality_rating && !ownerReviewData.communication_rating && !ownerReviewData.pet_behavior_rating) {
      setError('Please provide at least one rating.');
      return;
    }

    try {
      setReviewLoading(true);
      
      const reviewPayload = {
        service: selectedUpcomingService.id,
        punctuality_rating: ownerReviewData.punctuality_rating || 3,
        communication_rating: ownerReviewData.communication_rating || 3,
        pet_behavior_rating: ownerReviewData.pet_behavior_rating || 3,
        comment: ownerReviewData.comment,
        would_walk_for_again: ownerReviewData.would_walk_for_again,
        pets_match_description: ownerReviewData.pets_match_description,
        safe_neighborhood: ownerReviewData.safe_neighborhood
      };

      await apiService.createOwnerReview(reviewPayload);
      
      // Refresh the dashboard data to show the new review
      await fetchDashboardData();
      
      handleCloseOwnerReviewDialog();
      setSuccess('Owner review submitted successfully!');
    } catch (err) {
      console.error('Error submitting owner review:', err);
      setError('Failed to submit owner review. Please try again.');
    } finally {
      setReviewLoading(false);
    }
  };

  const handlePetReviewChange = (petId, field, value) => {
    setReviewData(prev => ({
      ...prev,
      [petId]: {
        ...prev[petId],
        [field]: value
      }
    }));
  };

  const handleSubmitReview = async () => {
    if (!selectedUpcomingService) return;

    // Check if at least one pet has a rating
    const petsWithRatings = selectedUpcomingService.pets?.filter(pet => 
      reviewData[pet.id]?.rating && reviewData[pet.id].rating > 0
    );

    if (!petsWithRatings || petsWithRatings.length === 0) {
      setError('Please provide at least one rating for a pet.');
      return;
    }

    try {
      setReviewLoading(true);
      
      // Submit pet reviews for each pet that has a rating
      const reviewPromises = petsWithRatings.map(pet => {
        const petReview = reviewData[pet.id];
        const reviewPayload = {
          pet: pet.id,
          service: selectedUpcomingService.id,
          rating: petReview.rating,
          energy_level_notes: petReview.energy_level_notes || '',
          socialization_notes: petReview.socialization_notes || '',
          obedience_notes: petReview.obedience_notes || '',
          other_notes: petReview.other_notes || '',
          would_service_again: petReview.would_service_again !== false,
          recommended_service_duration: petReview.recommended_service_duration || 30,
          special_considerations: petReview.special_considerations || ''
        };
        return apiService.createPetReview(reviewPayload);
      });

      await Promise.all(reviewPromises);
      
      // Refresh the dashboard data to show the new reviews
      await fetchDashboardData();
      
      handleCloseReviewDialog();
      setSuccess('Pet reviews submitted successfully!');
    } catch (err) {
      console.error('Error submitting pet reviews:', err);
      setError('Failed to submit pet reviews. Please try again.');
    } finally {
      setReviewLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'info';
      case 'in_progress': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatRating = (rating) => {
    if (!rating) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Star sx={{ fontSize: 16, color: 'gold' }} />
        <Typography variant="body2" sx={{ color: 'gold', fontWeight: 600 }}>
          {rating}/5
        </Typography>
      </Box>
    );
  };

  const formatReviewText = (service) => {
    const reviews = [];
    
    // Add owner review if exists
    if (service.handler_review) {
      reviews.push(`Owner Review: ${service.handler_review.rating}/5`);
    }
    
    // Add pet reviews if exist
    if (service.pet_reviews && service.pet_reviews.length > 0) {
      const petReviewTexts = service.pet_reviews.map(review => 
        `${review.pet?.name || 'Pet'} ${review.rating}/5`
      );
      reviews.push(`Pet Reviews: ${petReviewTexts.join(', ')}`);
    }
    
    return reviews.length > 0 ? reviews.join('\n') : null;
  };

  const renderPetPhotos = (pets, size = 56, borderColor = 'primary.main') => {
    if (!pets || pets.length === 0) return null;
    
    if (pets.length === 1) {
      return pets[0].photo ? (
        <Avatar 
          src={pets[0].photo}
          sx={{ 
            width: size, 
            height: size,
            border: 2,
            borderColor: borderColor
          }}
        >
          <Pets />
        </Avatar>
      ) : (
        <Avatar sx={{ bgcolor: theme.palette.info.main, width: size, height: size }}>
          <Pets />
        </Avatar>
      );
    }
    
    // Multiple pets - show first photo with count overlay
    const firstPet = pets[0];
    return (
      <Box sx={{ position: 'relative' }}>
        <Avatar 
          src={firstPet.photo}
          sx={{ 
            width: size, 
            height: size,
            border: 2,
            borderColor: borderColor
          }}
        >
          <Pets />
        </Avatar>
        {pets.length > 1 && (
          <Avatar 
            sx={{ 
              position: 'absolute',
              bottom: -4,
              right: -4,
              width: 24,
              height: 24,
              bgcolor: 'primary.main',
              fontSize: '0.75rem',
              fontWeight: 600
            }}
          >
            +{pets.length - 1}
          </Avatar>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ backgroundColor: INK, minHeight: 'calc(100vh - 64px)' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <SkeletonBlock dark width={300} height={30} radius={8} sx={{ mb: 1 }} />
          <SkeletonBlock dark width={220} height={18} sx={{ mb: 4 }} />
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
            {[0, 1, 2].map((i) => (
              <SkeletonCard key={i} dark sx={{ flex: '1 1 180px', minHeight: 150 }} />
            ))}
          </Box>
          <SkeletonCard sx={{ minHeight: 260 }} />
        </Container>
      </Box>
    );
  }

  return (
    // The dispatcher shell: ink ground, mint accents, light job cards that
    // pop against it. The flip is scoped to this page — shared handler-site
    // pages (auth, profile, payments) keep the paper ground.
    <Box sx={{ backgroundColor: INK, minHeight: 'calc(100vh - 64px)' }}>
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Header — sits directly on the ink ground, so colours are explicit. */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" component="h1" sx={{ color: PAPER, mb: 1 }}>
          Handler dashboard
        </Typography>
        <Typography variant="body1" sx={{ color: PAPER_MUTED }}>
          Howzit {user?.first_name} — ready for some walks?
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Availability Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={isAvailable}
                onChange={(e) => handleToggleAvailability(e.target.checked)}
                color="primary"
                disabled={actionLoading}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: PAPER }}>
                  Available for services
                </Typography>
                <Chip
                  label={isAvailable ? 'Online' : 'Offline'}
                  color={isAvailable ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            }
          />
      </Box>

        {/* Stat tiles — dark raised surfaces on the ink ground, per the
            dispatcher shell. Money reads in mono; mint is the money accent. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <Card sx={{ textAlign: 'center', p: 2, flex: 1, backgroundColor: INK_RAISED, borderColor: INK_LINE }}>
            <CardContent>
              <AccountBalanceWallet sx={{ fontSize: 40, color: MINT, mb: 1 }} />
              <Typography variant="h4" sx={{ ...moneySx, fontWeight: 700, mb: 1, color: PAPER }}>
                R{dashboardData.recent_earnings || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: PAPER_MUTED }}>
                Total earnings
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ textAlign: 'center', p: 2, flex: 1, backgroundColor: INK_RAISED, borderColor: INK_LINE }}>
            <CardContent>
              <Schedule sx={{ fontSize: 40, color: SHADOW_COBALT, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: PAPER }}>
                {upcomingServices.length}
              </Typography>
              <Typography variant="body2" sx={{ color: PAPER_MUTED }}>
                Upcoming services
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ textAlign: 'center', p: 2, flex: 1, backgroundColor: INK_RAISED, borderColor: INK_LINE }}>
            <CardContent>
              <Star sx={{ fontSize: 40, color: '#FFE9A8', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: PAPER }}>
                {dashboardData.avg_rating || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: PAPER_MUTED }}>
                Average rating
              </Typography>
            </CardContent>
          </Card>
        </Stack>

              {/* Active Service Section */}
        {activeService && (
          <Card sx={{ p: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Active Service in Progress
                    </Typography>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setActiveServiceDialogOpen(true)}
                startIcon={<Navigation />}
              >
                Manage Service
              </Button>
            </Box>
            <Typography variant="body1">
              {activeService.service_type?.name || 'Pet Service'} for {activeService.pets?.map(pet => pet.name).join(', ') || 'pets'}
            </Typography>
            <LinearProgress 
              variant="indeterminate" 
              sx={{ mt: 2, bgcolor: 'primary.dark' }}
            />
          </Card>
        )}

              {/* Service Opportunities */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ alignItems: 'flex-start' }}>
          {/* Service Opportunities */}
          <Card sx={{ p: 3, flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Service Opportunities
              </Typography>
              <Badge badgeContent={serviceRequests.length} color="primary">
                <Schedule />
              </Badge>
          </Box>

            {/* Location Message */}
                {locationMessage && (
              <Alert severity="info" sx={{ mb: 3 }}>
                      {locationMessage}
                  </Alert>
                )}

            {/* Radius Control */}
                {handlerLocation && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Search Radius: {maxDistance} km
                    </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[5, 10, 15, 25, 40].map(radius => (
                    <Button
                      key={radius}
                      size="small"
                      variant={maxDistance === radius ? 'contained' : 'outlined'}
                      onClick={() => handleDistanceFilterChange(radius)}
                      sx={{ textTransform: 'none' }}
                    >
                      {radius} km
                    </Button>
                  ))}
                </Box>
                  </Box>
                )}

            {serviceRequests.length > 0 ? (
              <List>
                {serviceRequests.map((request, index) => (
                  <React.Fragment key={`request-${request.id || request.booking?.id || index}`}>
                    <ListItem
                      sx={{ px: 0, cursor: 'pointer' }}
                      onClick={() => openRequestDialog(request)}
                    >
                      <ListItemAvatar>
                        {renderPetPhotos(request.booking.pets, 56, 'info.main')}
                      </ListItemAvatar>
                      
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Primary content */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {request.booking.service_type?.name || 'Pet Service'}
            </Typography>
                          {request.distance_km && (
                    <Chip
                              label={`${request.distance_km.toFixed(1)} km`} 
                              color="info" 
                              size="small"
                              sx={{ 
                                fontWeight: 600,
                                '& .MuiChip-label': {
                                  px: 1.5,
                                  fontSize: '0.75rem'
                                }
                              }}
                    />
                          )}
                  </Box>
                        
                        {/* Secondary content */}
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          👤 {request.booking.owner?.first_name} {request.booking.owner?.last_name}
                          </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          🐕 {request.booking.pets?.map(pet => `${pet.name}${pet.breed ? ` (${pet.breed})` : ''}`).join(', ') || 'Pets'}
                          </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          📅 {formatDate(request.booking.scheduled_date)} at {formatTime(request.booking.scheduled_time)}
                </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          ⏱️ {request.booking.duration_minutes || request.booking.duration} min • {request.booking.service_type?.category_name || 'Service'}
                </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          📍 {request.booking.owner?.addresses?.[0]?.formatted_address || 'Location not specified'}
                </Typography>
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, display: 'block' }}>
                          💰 R{request.booking.total_price}
                        </Typography>
                        {request.booking.notes && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                            📝 "{request.booking.notes}"
                          </Typography>
                        )}
                        </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                          size="small"
                  startIcon={<Check />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAcceptRequest(request.id);
                          }}
                          disabled={actionLoading}
                          title="Accept service request"
                          sx={{ 
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: '100px'
                          }}
                >
                          Accept
                </Button>
                      </Box>
                    </ListItem>
                    {index < serviceRequests.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No pending service requests
              </Typography>
            )}
          </Card>
                                  {/* Upcoming Services */}
          <Card sx={{ p: 3, flex: 1, minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              Upcoming Services
            </Typography>
            {upcomingServices.length > 0 ? (
              <List>
                {upcomingServices.map((service, index) => (
                  <React.Fragment key={`upcoming-${service.id || index}`}>
                    <ListItem sx={{ px: 0, cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }} onClick={() => handleViewUpcomingServiceDetails(service)}>
                      <ListItemAvatar>
                        {renderPetPhotos(service.pets, 56, 'primary.main')}
                      </ListItemAvatar>
                      
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Primary content */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                          {service.service_type?.name || 'Pet Service'}
          </Typography>
                        
                        {/* Secondary content */}
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          👤 {service.owner?.first_name} {service.owner?.last_name}
              </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          🐕 {service.pets?.map(pet => `${pet.name}${pet.breed ? ` (${pet.breed})` : ''}`).join(', ') || 'Pets'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          📅 {formatDate(service.scheduled_date)} at {formatTime(service.scheduled_time)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          ⏱️ {service.duration_minutes || service.duration} min • {service.service_type?.category_name || 'Service'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          📍 {service.owner?.addresses?.[0]?.formatted_address || 'Location not specified'}
                        </Typography>
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, display: 'block' }}>
                          💰 R{service.total_price}
                        </Typography>
                        {service.notes && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                            📝 "{service.notes}"
                          </Typography>
                        )}
            </Box>
            
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button 
              variant="contained" 
                          size="small"
                          startIcon={<PlayArrow />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartService(service.id);
                          }}
                          disabled={actionLoading}
                          title="Start service"
                          sx={{ 
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: '110px'
                          }}
            >
                          Start Service
            </Button>
                        <IconButton size="small">
                          <Phone />
                        </IconButton>
          </Box>
                    </ListItem>
                    {index < upcomingServices.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No upcoming services
              </Typography>
          )}
        </Card>
        </Stack>

        {/* Completed Services Section */}
        <Card sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            Recent Completed Services
            </Typography>
          {completedServices.length > 0 ? (
                                <List>
              {completedServices.map((service, index) => (
                <React.Fragment key={`completed-${service.id || index}`}>
                      <ListItem
                    sx={{ px: 0, cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
                    onClick={() => handleViewServiceDetails(service.id)}
                      >
                        <ListItemAvatar>
                      {renderPetPhotos(service.pets, 56, 'success.main')}
                        </ListItemAvatar>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Primary content */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {service.service_type?.name || 'Pet Service'}
                            </Typography>
                        {service.handler_review && formatRating(service.handler_review.rating)}
                      </Box>
                      
                      {/* Secondary content */}
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                        👤 {service.owner?.first_name} {service.owner?.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                        🐕 {service.pets?.map(pet => `${pet.name}${pet.breed ? ` (${pet.breed})` : ''}`).join(', ') || 'Pets'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                        📅 {formatDate(service.scheduled_date)} at {formatTime(service.scheduled_time)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                        ⏱️ {service.duration_minutes || service.duration} min • {service.service_type?.category_name || 'Service'}
                      </Typography>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, display: 'block' }}>
                        💰 R{service.total_price}
                      </Typography>
                                              {formatReviewText(service) && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 1, whiteSpace: 'pre-line' }}>
                            📝 {formatReviewText(service)}
                          </Typography>
                        )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                      <Chip
                        label="Completed" 
                        color="success"
                        size="small"
                        sx={{ 
                          fontWeight: 600,
                          '& .MuiChip-label': {
                            px: 1.5,
                            fontSize: '0.75rem'
                          }
                        }}
                      />
                                              {!service.handler_review && !service.pet_reviews?.length && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Star />}
                            onClick={async (e) => {
                              e.stopPropagation();
                              await handleOpenReviewDialog(service);
                            }}
                            sx={{ 
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              py: 0.5,
                              px: 1
                            }}
                          >
                            Review
                          </Button>
                        )}
                        {!service.owner_review && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Person />}
                            onClick={async (e) => {
                              e.stopPropagation();
                              await handleOpenOwnerReviewDialog(service);
                            }}
                            sx={{ 
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              py: 0.5,
                              px: 1,
                              mt: 0.5
                            }}
                          >
                            Owner Review
                          </Button>
                        )}
                        {service.pet_reviews?.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Pet reviews submitted
                            </Typography>
                            <Star sx={{ fontSize: '0.75rem', color: 'warning.main' }} />
                          </Box>
                        )}
                    </Box>
                  </ListItem>
                  {index < completedServices.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No completed services yet
            </Typography>
          )}
        </Card>
      </Stack>

      {/* Request Details Dialog */}
      <Dialog 
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Service Request Details</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedRequest.booking.service_type?.name || 'Pet Service'}
              </Typography>
              
              {/* Service Details */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Service Details</Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Category:</strong> {selectedRequest.booking.service_type?.category_name || 'General'}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Duration:</strong> {selectedRequest.booking.duration_minutes || selectedRequest.booking.duration} minutes
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Date & Time:</strong> {formatDate(selectedRequest.booking.scheduled_date)} at {formatTime(selectedRequest.booking.scheduled_time)}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Price:</strong> R{selectedRequest.booking.total_price}
                </Typography>
                {selectedRequest.distance_km && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Distance:</strong> {selectedRequest.distance_km.toFixed(2)} km away
                  </Typography>
                )}
              </Box>

              {/* Owner Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Owner Information</Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Name:</strong> {selectedRequest.booking.owner?.first_name} {selectedRequest.booking.owner?.last_name}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Email:</strong> {selectedRequest.booking.owner?.email || 'Not provided'}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Phone:</strong> {selectedRequest.booking.owner?.phone || 'Not provided'}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Address:</strong> {selectedRequest.booking.owner?.addresses?.[0]?.formatted_address || 'Not provided'}
                </Typography>
              </Box>

              {/* Pets Information */}
              {selectedRequest.booking.pets && selectedRequest.booking.pets.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Pets</Typography>
                  <Grid container spacing={2}>
                    {selectedRequest.booking.pets.map((pet, index) => (
                      <Grid item xs={12} sm={6} key={pet.id || index}>
                        <Card sx={{ p: 2, height: '100%' }}>
                          {/* Pet Photo */}
                          {pet.photo && (
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                              <img 
                                src={pet.photo} 
                                alt={`${pet.name}`}
                                style={{ 
                                  width: '100%', 
                                  maxWidth: '200px', 
                                  height: '150px', 
                                  objectFit: 'cover',
                                  borderRadius: '8px'
                                }}
                              />
                            </Box>
                          )}
                          
                          {/* Pet Details */}
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            {pet.name}
                          </Typography>
                          
                          <Stack spacing={1}>
                            {pet.breed && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                                  Breed:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {pet.breed}
                                </Typography>
                              </Box>
                            )}
                            
                            {pet.age_display && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                                  Age:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {pet.age_display}
                                </Typography>
                              </Box>
                            )}
                            
                            {pet.weight && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                                  Weight:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {pet.weight} kg
                                </Typography>
                              </Box>
                            )}
                            
                            {pet.size_display && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                                  Size:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {pet.size_display}
                                </Typography>
                              </Box>
                            )}
                            
                            {pet.energy_level && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                                  Energy:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {pet.energy_level}
                                </Typography>
                              </Box>
                            )}
                            
                            {pet.temperament && pet.temperament.length > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                                  Temperament:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {pet.temperament.map((temp, tempIndex) => (
                                    <Chip 
                                      key={tempIndex} 
                                      label={temp} 
                                      size="small" 
                                      color="primary" 
                              variant="outlined"
                                      sx={{ fontSize: '0.7rem' }}
                            />
                                  ))}
                          </Box>
                              </Box>
                            )}
                            
                            {pet.is_spayed_neutered !== undefined && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                                  Spayed/Neutered:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {pet.is_spayed_neutered ? 'Yes' : 'No'}
                              </Typography>
                            </Box>
                            )}
                            
                            {pet.is_vaccinated !== undefined && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                                  Vaccinated:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {pet.is_vaccinated ? 'Yes' : 'No'}
                              </Typography>
                            </Box>
                            )}
                            
                            {pet.medical_conditions && (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                                  Medical:
                                </Typography>
                                <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
                                  {pet.medical_conditions}
                                </Typography>
                              </Box>
                            )}
                            
                            {pet.special_instructions && (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                                  Special:
                                </Typography>
                                <Typography variant="body2" color="info.main" sx={{ fontWeight: 500 }}>
                                  {pet.special_instructions}
                                </Typography>
                              </Box>
                            )}
                            
                            {pet.emergency_contact_name && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                                  Emergency:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {pet.emergency_contact_name}
                                  {pet.emergency_contact_phone && ` (${pet.emergency_contact_phone})`}
                                </Typography>
                              </Box>
                            )}
                            
                            {pet.handler_rating && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                                  Rating:
                                </Typography>
                                {formatRating(pet.handler_rating)}
                                {pet.handler_review_count && (
                              <Typography variant="body2" color="text.secondary">
                                    ({pet.handler_review_count} reviews)
                              </Typography>
                                )}
                            </Box>
                            )}
                          </Stack>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                          </Box>
              )}

              {/* Special Instructions */}
              {selectedRequest.booking.notes && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Special Instructions</Typography>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      "{selectedRequest.booking.notes}"
                    </Typography>
                        </Box>
                </Box>
              )}

              {/* Handler Requirements */}
              {selectedRequest.booking.service_type?.requirements && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Service Requirements</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRequest.booking.service_type.requirements}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialogOpen(false)}>
            Close
          </Button>
                          <Button
                            variant="contained"
                            color="success"
            onClick={() => handleAcceptRequest(selectedRequest?.id)}
                            disabled={actionLoading}
                            startIcon={<Check />}
                          >
            Accept Service
                          </Button>
        </DialogActions>
      </Dialog>

      {/* Active Service Management Dialog */}
      <Dialog
        open={activeServiceDialogOpen}
        onClose={() => setActiveServiceDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Active Service: {activeService?.service_type?.name || 'Pet Service'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            {/* Service Updates */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Service Updates
              </Typography>
              <Box sx={{ height: '100%', minHeight: 300, mb: 3 }}>
                {activeService?.id && (
                  <LiveTracking
                    bookingId={activeService.id}
                    isHandler={true}
                    canShare={activeService?.status === 'in_progress'}
                  />
                )}
                <ServiceUpdatesChat
                  updates={serviceUpdates}
                  currentUserId={user?.id}
                  ownerName={activeService?.owner ? `${activeService.owner.first_name} ${activeService.owner.last_name}` : 'Owner'}
                  handlerName={activeService?.handler ? `${activeService.handler.first_name} ${activeService.handler.last_name}` : 'Handler'}
                          />
                        </Box>
            </Box>

            {/* Send Update Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Send Update
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Send a message to the pet owner..."
                value={updateMessage}
                onChange={(e) => setUpdateMessage(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          <Button
                            variant="outlined"
                  startIcon={<Message />}
                  onClick={() => handleSendUpdate('message')}
                  disabled={!updateMessage || actionLoading}
                >
                  Send Message
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LocationOn />}
                  onClick={() => handleSendUpdate('location')}
                  disabled={!currentLocation || actionLoading}
                >
                  Send Location
                </Button>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="photo-upload"
                  type="file"
                  onChange={handlePhotoUpload}
                />
                <label htmlFor="photo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<PhotoCamera />}
                  >
                    Take Photo
                  </Button>
                </label>
                {updatePhoto && (
                  <Button
                    variant="contained"
                    startIcon={<Send />}
                    onClick={() => handleSendUpdate('photo')}
                            disabled={actionLoading}
                          >
                    Send Photo
                          </Button>
                )}
                        </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveServiceDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Stop />}
            onClick={() => handleCompleteService(activeService?.id)}
            disabled={actionLoading}
          >
            Complete Service
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Active Service */}
      {activeService && !activeServiceDialogOpen && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => setActiveServiceDialogOpen(true)}
        >
          <Navigation />
        </Fab>
      )}

      {/* Service Details Dialog */}
      <Dialog
        open={serviceDetailsOpen}
        onClose={handleCloseServiceDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Service Details</Typography>
            {selectedServiceDetails && (
              <Typography variant="subtitle2" color="text.secondary">
                {selectedServiceDetails.service_type?.name || 'Pet Service'} for {selectedServiceDetails.owner?.first_name} {selectedServiceDetails.owner?.last_name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleCloseServiceDetails}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedServiceDetails && (
            <Grid container spacing={3}>
              {/* Summary Metrics */}
              <Grid size={12}>
                <Card sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'inherit' }}>Service Summary</Typography>
                  <Grid container spacing={2}>
                    <Grid size={3}>
                      <Typography variant="body2" sx={{ color: 'inherit', opacity: 0.8 }}>Earnings</Typography>
                      <Typography variant="h6" sx={{ color: 'inherit', fontWeight: 600 }}>
                        R{selectedServiceDetails.total_price}
              </Typography>
                    </Grid>
                    <Grid size={3}>
                      <Typography variant="body2" sx={{ color: 'inherit', opacity: 0.8 }}>Duration</Typography>
                      <Typography variant="h6" sx={{ color: 'inherit', fontWeight: 600 }}>
                        {selectedServiceDetails.actual_duration_minutes || selectedServiceDetails.duration_minutes || selectedServiceDetails.duration} min
                </Typography>
                    </Grid>
                    <Grid size={3}>
                      <Typography variant="body2" sx={{ color: 'inherit', opacity: 0.8 }}>Pets</Typography>
                      <Typography variant="h6" sx={{ color: 'inherit', fontWeight: 600 }}>
                        {selectedServiceDetails.pets?.length || 0}
                              </Typography>
                    </Grid>
                    <Grid size={3}>
                      <Typography variant="body2" sx={{ color: 'inherit', opacity: 0.8 }}>Rating</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {selectedServiceDetails.handler_review ? (
                          formatRating(selectedServiceDetails.handler_review.rating)
                        ) : (
                          <Typography variant="h6" sx={{ color: 'inherit', fontWeight: 600 }}>
                            -
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* Service Summary */}
              <Grid size={12}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Service Summary</Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Date & Time</Typography>
                      <Typography variant="body1">{formatDate(selectedServiceDetails.scheduled_date)} at {formatTime(selectedServiceDetails.scheduled_time)}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body1">{selectedServiceDetails.duration_minutes || selectedServiceDetails.duration} minutes</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                              <Chip
                        label={selectedServiceDetails.status} 
                        color={getStatusColor(selectedServiceDetails.status)} 
                                size="small"
                      />
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Service Type</Typography>
                      <Typography variant="body1">{selectedServiceDetails.service_type?.name || 'Pet Service'}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Category</Typography>
                      <Typography variant="body1">{selectedServiceDetails.service_type?.category_name || 'General'}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Pickup Location</Typography>
                      <Typography variant="body1">{selectedServiceDetails.pickup_address || selectedServiceDetails.owner?.addresses?.[0]?.formatted_address || 'Not specified'}</Typography>
                    </Grid>
                    {selectedServiceDetails.notes && (
                      <Grid size={12}>
                        <Typography variant="body2" color="text.secondary">Special Instructions</Typography>
                        <Typography variant="body1">{selectedServiceDetails.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Card>
              </Grid>

              {/* Owner Information */}
              <Grid size={12}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Owner Information</Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Name</Typography>
                      <Typography variant="body1">{selectedServiceDetails.owner?.first_name} {selectedServiceDetails.owner?.last_name}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{selectedServiceDetails.owner?.email || 'Not provided'}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">{selectedServiceDetails.owner?.phone || 'Not provided'}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Address</Typography>
                      <Typography variant="body1">{selectedServiceDetails.owner?.addresses?.[0]?.formatted_address || 'Not provided'}</Typography>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* Pets Information */}
              {selectedServiceDetails.pets && selectedServiceDetails.pets.length > 0 && (
                <Grid size={12}>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Pets</Typography>
                    <Grid container spacing={2}>
                      {selectedServiceDetails.pets.map((pet, index) => (
                        <Grid item xs={12} sm={6} key={pet.id || index}>
                          <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            {/* Pet Photo */}
                            {pet.photo && (
                              <Box sx={{ mb: 2, textAlign: 'center' }}>
                                <img 
                                  src={pet.photo} 
                                  alt={`${pet.name}`}
                                  style={{ 
                                    width: '100%', 
                                    maxWidth: '150px', 
                                    height: '120px', 
                                    objectFit: 'cover',
                                    borderRadius: '8px'
                                  }}
                              />
                            </Box>
                            )}
                            
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                              {pet.name}
                            </Typography>
                            
                            <Stack spacing={0.5}>
                              {pet.breed && (
                                <Typography variant="body2" color="text.secondary">
                                  Breed: {pet.breed}
                                </Typography>
                              )}
                              {pet.age_display && (
                                <Typography variant="body2" color="text.secondary">
                                  Age: {pet.age_display}
                                </Typography>
                              )}
                              {pet.weight && (
                                <Typography variant="body2" color="text.secondary">
                                  Weight: {pet.weight} kg
                                </Typography>
                              )}
                              {pet.size_display && (
                                <Typography variant="body2" color="text.secondary">
                                  Size: {pet.size_display}
                                </Typography>
                              )}
                              {pet.energy_level && (
                                <Typography variant="body2" color="text.secondary">
                                  Energy: {pet.energy_level}
                                </Typography>
                              )}
                              {pet.medical_conditions && (
                                <Typography variant="body2" color="warning.main">
                                  Medical: {pet.medical_conditions}
                                </Typography>
                              )}
                              {pet.special_instructions && (
                                <Typography variant="body2" color="info.main">
                                  Special: {pet.special_instructions}
                                </Typography>
                              )}
                            </Stack>
                              </Box>
                        </Grid>
                  ))}
                    </Grid>
                </Card>
              </Grid>
              )}

              {/* Earnings Details */}
              <Grid size={12}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Earnings & Timing</Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Scheduled Start</Typography>
                      <Typography variant="body1">{formatDate(selectedServiceDetails.scheduled_date)} at {formatTime(selectedServiceDetails.scheduled_time)}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Actual Start</Typography>
                      <Typography variant="body1">
                        {selectedServiceDetails.actual_start_time ? 
                          formatDate(selectedServiceDetails.actual_start_time) + ' at ' + formatTime(selectedServiceDetails.actual_start_time) : 
                          'Not recorded'
                        }
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Actual End</Typography>
                      <Typography variant="body1">
                        {selectedServiceDetails.actual_end_time ? 
                          formatDate(selectedServiceDetails.actual_end_time) + ' at ' + formatTime(selectedServiceDetails.actual_end_time) : 
                          'Not recorded'
                        }
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body1">
                        {selectedServiceDetails.actual_duration_minutes ? 
                          `${selectedServiceDetails.actual_duration_minutes} min (actual)` : 
                          `${selectedServiceDetails.duration_minutes || selectedServiceDetails.duration} min (scheduled)`
                        }
                      </Typography>
          </Grid>
      </Grid>

                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Payment Breakdown</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Base Price:</Typography>
                    <Typography variant="body2">R{selectedServiceDetails.base_price || selectedServiceDetails.total_price}</Typography>
                  </Box>
                  {selectedServiceDetails.additional_pet_fee && parseFloat(selectedServiceDetails.additional_pet_fee) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Additional Pet Fee:</Typography>
                      <Typography variant="body2">R{selectedServiceDetails.additional_pet_fee}</Typography>
                    </Box>
                  )}
                  {selectedServiceDetails.tip_amount && parseFloat(selectedServiceDetails.tip_amount) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Tip:</Typography>
                      <Typography variant="body2" color="success.main">R{selectedServiceDetails.tip_amount}</Typography>
                    </Box>
                  )}
                  {selectedServiceDetails.cancellation_fee && parseFloat(selectedServiceDetails.cancellation_fee) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="error.main">Cancellation Fee:</Typography>
                      <Typography variant="body2" color="error.main">-R{selectedServiceDetails.cancellation_fee}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>Total Earned:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>R{selectedServiceDetails.total_price}</Typography>
                  </Box>
                </Card>
              </Grid>

              {/* Service Performance Metrics */}
            <Grid size={12}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Service Performance</Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Scheduled Start</Typography>
                      <Typography variant="body1">{formatDate(selectedServiceDetails.scheduled_date)} at {formatTime(selectedServiceDetails.scheduled_time)}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Actual Start</Typography>
                      <Typography variant="body1">
                        {selectedServiceDetails.actual_start_time ? 
                          formatDate(selectedServiceDetails.actual_start_time) + ' at ' + formatTime(selectedServiceDetails.actual_start_time) : 
                          'Not recorded'
                        }
          </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Actual End</Typography>
                      <Typography variant="body1">
                        {selectedServiceDetails.actual_end_time ? 
                          formatDate(selectedServiceDetails.actual_end_time) + ' at ' + formatTime(selectedServiceDetails.actual_end_time) : 
                          'Not recorded'
                        }
            </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body1">
                        {selectedServiceDetails.actual_duration_minutes ? 
                          `${selectedServiceDetails.actual_duration_minutes} min (actual)` : 
                          `${selectedServiceDetails.duration_minutes || selectedServiceDetails.duration} min (scheduled)`
                        }
                            </Typography>
                    </Grid>
                    {selectedServiceDetails.handler_review && (
                      <>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Owner Rating</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {formatRating(selectedServiceDetails.handler_review.rating)}
                          </Box>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Review Date</Typography>
                          <Typography variant="body1">{formatDate(selectedServiceDetails.handler_review.created_at)}</Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Card>
              </Grid>

              {/* Reviews Summary */}
              {(selectedServiceDetails.handler_review || (selectedServiceDetails.pet_reviews && selectedServiceDetails.pet_reviews.length > 0)) && (
                <Grid size={12}>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Your Reviews</Typography>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {formatReviewText(selectedServiceDetails) && (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line', fontWeight: 500 }}>
                          {formatReviewText(selectedServiceDetails)}
                        </Typography>
                      )}
                    </Box>
                  </Card>
                </Grid>
              )}

              {/* Pet Reviews (if available) */}
              {selectedServiceDetails.pet_reviews && selectedServiceDetails.pet_reviews.length > 0 && (
                <Grid size={12}>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Pet Reviews</Typography>
                    <Stack spacing={2}>
                      {selectedServiceDetails.pet_reviews.map((review, index) => (
                        <Box key={review.id || index} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            {review.pet?.photo && (
                              <Avatar 
                                src={review.pet.photo}
                                sx={{ width: 40, height: 40 }}
                              >
                                {review.pet.name?.[0] || 'P'}
                              </Avatar>
                            )}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {review.pet?.name || 'Pet'}
                            </Typography>
                            {formatRating(review.rating)}
                          </Box>
                          
                          {/* Review Notes */}
                          <Stack spacing={1} sx={{ mt: 2 }}>
                            {review.energy_level_notes && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Energy Level:
                                </Typography>
                                <Typography variant="body2">{review.energy_level_notes}</Typography>
                              </Box>
                            )}
                            {review.socialization_notes && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Socialization:
                                </Typography>
                                <Typography variant="body2">{review.socialization_notes}</Typography>
                              </Box>
                            )}
                            {review.obedience_notes && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Obedience:
                                </Typography>
                                <Typography variant="body2">{review.obedience_notes}</Typography>
                              </Box>
                            )}
                            {review.other_notes && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Other Notes:
                                </Typography>
                                <Typography variant="body2">{review.other_notes}</Typography>
                              </Box>
                            )}
                            {review.special_considerations && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Special Considerations:
                                </Typography>
                                <Typography variant="body2">{review.special_considerations}</Typography>
                              </Box>
                            )}
                          </Stack>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary">
                              Would service again: {review.would_service_again ? 'Yes' : 'No'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Recommended duration: {review.recommended_service_duration} min
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(review.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Card>
                </Grid>
              )}

              {/* Service Updates */}
              <Grid size={12}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Service Updates</Typography>
                  <Box sx={{ height: '100%', minHeight: 400 }}>
                    <ServiceUpdatesChat
                      updates={serviceDetailsUpdates}
                      currentUserId={user?.id}
                      ownerName={selectedServiceDetails?.owner ? `${selectedServiceDetails.owner.first_name} ${selectedServiceDetails.owner.last_name}` : 'Owner'}
                      handlerName={selectedServiceDetails?.handler ? `${selectedServiceDetails.handler.first_name} ${selectedServiceDetails.handler.last_name}` : 'Handler'}
                              />
                  </Box>
      </Card>
            </Grid>
          </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseServiceDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Upcoming Service Details Dialog */}
      <Dialog
        open={upcomingServiceDetailsOpen}
        onClose={handleCloseUpcomingServiceDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Upcoming Service Details</Typography>
            {selectedUpcomingService && (
              <Typography variant="subtitle2" color="text.secondary">
                {selectedUpcomingService.service_type?.name || 'Pet Service'} for {selectedUpcomingService.owner?.first_name} {selectedUpcomingService.owner?.last_name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleCloseUpcomingServiceDetails}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedUpcomingService && (
            <Grid container spacing={3}>
              {/* Service Summary */}
              <Grid size={12}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Service Summary</Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Date & Time</Typography>
                      <Typography variant="body1">{formatDate(selectedUpcomingService.scheduled_date)} at {formatTime(selectedUpcomingService.scheduled_time)}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body1">{selectedUpcomingService.duration_minutes || selectedUpcomingService.duration} minutes</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip 
                        label={selectedUpcomingService.status} 
                        color={getStatusColor(selectedUpcomingService.status)} 
                        size="small" 
                      />
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Service Type</Typography>
                      <Typography variant="body1">{selectedUpcomingService.service_type?.name || 'Pet Service'}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Category</Typography>
                      <Typography variant="body1">{selectedUpcomingService.service_type?.category_name || 'General'}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Pickup Location</Typography>
                      <Typography variant="body1">{selectedUpcomingService.owner?.addresses?.[0]?.formatted_address || 'Not specified'}</Typography>
                    </Grid>
                    {selectedUpcomingService.notes && (
                      <Grid size={12}>
                        <Typography variant="body2" color="text.secondary">Special Instructions</Typography>
                        <Typography variant="body1">{selectedUpcomingService.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Card>
              </Grid>

              {/* Owner Information */}
              <Grid size={12}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Owner Information</Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Name</Typography>
                      <Typography variant="body1">{selectedUpcomingService.owner?.first_name} {selectedUpcomingService.owner?.last_name}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{selectedUpcomingService.owner?.email || 'Not provided'}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">{selectedUpcomingService.owner?.phone || 'Not provided'}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Address</Typography>
                      <Typography variant="body1">{selectedUpcomingService.owner?.addresses?.[0]?.formatted_address || 'Not provided'}</Typography>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* Pets Information */}
              {selectedUpcomingService.pets && selectedUpcomingService.pets.length > 0 && (
                <Grid size={12}>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Pets</Typography>
                    <Grid container spacing={2}>
                      {selectedUpcomingService.pets.map((pet, index) => (
                        <Grid item xs={12} sm={6} key={pet.id || index}>
                          <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            {/* Pet Photo */}
                            {pet.photo && (
                              <Box sx={{ mb: 2, textAlign: 'center' }}>
                                <img 
                                  src={pet.photo} 
                                  alt={`${pet.name}`}
                                  style={{ 
                                    width: '100%', 
                                    maxWidth: '150px', 
                                    height: '120px', 
                                    objectFit: 'cover',
                                    borderRadius: '8px'
                                  }}
                                />
                </Box>
                            )}
                            
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                              {pet.name}
                            </Typography>
                            
                            <Stack spacing={0.5}>
                              {pet.breed && (
                      <Typography variant="body2" color="text.secondary">
                                  Breed: {pet.breed}
                      </Typography>
                              )}
                              {pet.age_display && (
                                <Typography variant="body2" color="text.secondary">
                                  Age: {pet.age_display}
                                </Typography>
                              )}
                              {pet.weight && (
                                <Typography variant="body2" color="text.secondary">
                                  Weight: {pet.weight} kg
                                </Typography>
                              )}
                              {pet.size_display && (
                                <Typography variant="body2" color="text.secondary">
                                  Size: {pet.size_display}
                                </Typography>
                              )}
                              {pet.energy_level && (
                                <Typography variant="body2" color="text.secondary">
                                  Energy: {pet.energy_level}
                                </Typography>
                              )}
                              {pet.medical_conditions && (
                                <Typography variant="body2" color="warning.main">
                                  Medical: {pet.medical_conditions}
                                </Typography>
                              )}
                              {pet.special_instructions && (
                                <Typography variant="body2" color="info.main">
                                  Special: {pet.special_instructions}
                                </Typography>
                              )}
                            </Stack>
                    </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Card>
                </Grid>
              )}

              {/* Earnings Details */}
              <Grid size={12}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Earnings</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Base Price:</Typography>
                    <Typography variant="body2">R{selectedUpcomingService.base_price || selectedUpcomingService.total_price}</Typography>
                </Box>
                  {selectedUpcomingService.additional_pet_fee && parseFloat(selectedUpcomingService.additional_pet_fee) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Additional Pet Fee:</Typography>
                      <Typography variant="body2">R{selectedUpcomingService.additional_pet_fee}</Typography>
              </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>Total to Earn:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>R{selectedUpcomingService.total_price}</Typography>
            </Box>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpcomingServiceDetails}>
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrow />}
            onClick={() => {
              handleStartService(selectedUpcomingService?.id);
              handleCloseUpcomingServiceDetails();
            }}
            disabled={actionLoading}
          >
            Start Service
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={handleCloseReviewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Review Service - Rate Each Pet</Typography>
            <IconButton onClick={handleCloseReviewDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUpcomingService && selectedUpcomingService.pets && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please rate each pet you provided service for. You can review multiple pets from this service.
              </Typography>
              
              {selectedUpcomingService.pets.map((pet, index) => (
                <Card key={pet.id} sx={{ mb: 3, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {/* Pet Photo */}
                    {pet.photo ? (
                      <Avatar 
                        src={pet.photo}
                        sx={{ width: 60, height: 60 }}
                      />
                    ) : (
                      <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}>
                        <Pets />
                      </Avatar>
                    )}
                    
                    {/* Pet Info */}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {pet.name}
                      </Typography>
                      {pet.breed && (
                        <Typography variant="body2" color="text.secondary">
                          {pet.breed}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Overall Rating */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Overall Rating for {pet.name}:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star}
                          sx={{ 
                            fontSize: 24, 
                            color: (reviewData[pet.id]?.rating || 0) >= star ? 'gold' : 'grey.400', 
                            cursor: 'pointer',
                            transition: 'color 0.2s ease'
                          }} 
                          onClick={() => handlePetReviewChange(pet.id, 'rating', star)}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Detailed Notes */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Energy Level Notes"
                        placeholder={`How was ${pet.name}'s energy level?`}
                        value={reviewData[pet.id]?.energy_level_notes || ''}
                        onChange={(e) => handlePetReviewChange(pet.id, 'energy_level_notes', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Socialization Notes"
                        placeholder={`How did ${pet.name} interact with others?`}
                        value={reviewData[pet.id]?.socialization_notes || ''}
                        onChange={(e) => handlePetReviewChange(pet.id, 'socialization_notes', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Obedience Notes"
                        placeholder={`How well did ${pet.name} follow commands?`}
                        value={reviewData[pet.id]?.obedience_notes || ''}
                        onChange={(e) => handlePetReviewChange(pet.id, 'obedience_notes', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Other Notes"
                        placeholder={`Any other observations about ${pet.name}?`}
                        value={reviewData[pet.id]?.other_notes || ''}
                        onChange={(e) => handlePetReviewChange(pet.id, 'other_notes', e.target.value)}
                      />
                    </Grid>
                  </Grid>

                  {/* Would Service Again */}
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={reviewData[pet.id]?.would_service_again !== false}
                          onChange={(e) => handlePetReviewChange(pet.id, 'would_service_again', e.target.checked)}
                        />
                      }
                      label="Would provide this service again for this pet"
                    />
                  </Box>

                  {/* Special Considerations */}
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Special considerations for future services..."
                    value={reviewData[pet.id]?.special_considerations || ''}
                    onChange={(e) => handlePetReviewChange(pet.id, 'special_considerations', e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  
                  {index < selectedUpcomingService.pets.length - 1 && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReviewDialog} disabled={reviewLoading}>Cancel</Button>
          <Button 
            onClick={handleSubmitReview} 
            variant="contained" 
            disabled={reviewLoading}
            startIcon={<CheckCircle />}
          >
            {reviewLoading ? <CircularProgress size={20} /> : 'Submit Reviews'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Owner Review Dialog */}
      <Dialog
        open={ownerReviewDialogOpen}
        onClose={handleCloseOwnerReviewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Review Owner & Service</Typography>
            <IconButton onClick={handleCloseOwnerReviewDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUpcomingService && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                This review is for internal use only and helps us understand the service experience from your perspective.
              </Alert>
              
              <Typography variant="h6" sx={{ mb: 2 }}>
                Review for {selectedUpcomingService.owner?.first_name} {selectedUpcomingService.owner?.last_name}
              </Typography>

              {/* Ratings */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Owner Punctuality
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        sx={{ 
                          fontSize: 20, 
                          color: ownerReviewData.punctuality_rating >= star ? 'gold' : 'grey.400', 
                          cursor: 'pointer',
                          transition: 'color 0.2s ease'
                        }} 
                        onClick={() => handleOwnerReviewChange('punctuality_rating', star)}
                      />
                    ))}
                  </Box>
                </Grid>
                
                <Grid size={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Communication
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        sx={{ 
                          fontSize: 20, 
                          color: ownerReviewData.communication_rating >= star ? 'gold' : 'grey.400', 
                          cursor: 'pointer',
                          transition: 'color 0.2s ease'
                        }} 
                        onClick={() => handleOwnerReviewChange('communication_rating', star)}
                      />
                    ))}
                  </Box>
                </Grid>
                
                <Grid size={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Pet Behavior
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        sx={{ 
                          fontSize: 20, 
                          color: ownerReviewData.pet_behavior_rating >= star ? 'gold' : 'grey.400', 
                          cursor: 'pointer',
                          transition: 'color 0.2s ease'
                        }} 
                        onClick={() => handleOwnerReviewChange('pet_behavior_rating', star)}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>

              {/* Checkboxes */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={ownerReviewData.would_walk_for_again}
                        onChange={(e) => handleOwnerReviewChange('would_walk_for_again', e.target.checked)}
                      />
                    }
                    label="Would walk for again"
                  />
                </Grid>
                <Grid size={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={ownerReviewData.pets_match_description}
                        onChange={(e) => handleOwnerReviewChange('pets_match_description', e.target.checked)}
                      />
                    }
                    label="Pets match description"
                  />
                </Grid>
                <Grid size={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={ownerReviewData.safe_neighborhood}
                        onChange={(e) => handleOwnerReviewChange('safe_neighborhood', e.target.checked)}
                      />
                    }
                    label="Safe neighborhood"
                  />
                </Grid>
              </Grid>

              {/* Comments */}
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Internal notes about the owner/pet (optional)"
                value={ownerReviewData.comment}
                onChange={(e) => handleOwnerReviewChange('comment', e.target.value)}
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOwnerReviewDialog} disabled={reviewLoading}>Cancel</Button>
          <Button 
            onClick={handleSubmitOwnerReview} 
            variant="contained" 
            disabled={reviewLoading}
            startIcon={<CheckCircle />}
          >
            {reviewLoading ? <CircularProgress size={20} /> : 'Submit Review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </Box>
  );
};

export default HandlerDashboard;
